import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import { MAX_EXPLORE_ROUNDS } from '@/api/v4/genai-request-history.api';
import { FixedPanelWithShadowLayer } from '@/components/fixed-panel-with-shadow-layer/fixed-panel-with-shadow-layer';
import { isDefaultOptionArray } from '@/helpers/other';
import { useSidebars } from '@/hooks/use-sidebars';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { modalState } from '@/state/modal.state';
import { colors } from '@/theme/colors';
import type { IOption } from '@/types';
import { Button } from '@/ui/button/button';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { Icon } from '@/ui/icons/icon';
import { Stack } from '@/ui/line/line';
import { useAtom } from 'jotai';

import {
  CLOSED_NAVBAR_WIDTH,
  OPEN_NAVBAR_WIDTH,
} from '../../navbar/navbar.styles';
import type { ExampleCompany } from '../explore.state';
import { ExampleCompaniesSelect } from '../form/example-companies-select';
import { LocationSelect } from '../form/location-select';
import { getPendingFilterDeltas } from '../refine/explore-round-display';
import { useSubmitExploreRound } from '../refine/use-submit-explore-round';

import { useRegenerateExploreForm } from './use-regenerate-explore-form';

const filterFieldBackground = colors.blue.c9;
const filterFieldBorderColor = colors.gray.c4;

export const RegeneratePanel = ({
  defaultValues,
  isLoading,
  latestRoundNumber,
  onClose,
  requestId,
  viewingRoundNumber,
}: {
  defaultValues: {
    defaultDescription: string;
    defaultExampleCompanies: ExampleCompany[];
    defaultLocations: IOption[];
  };
  isLoading: boolean;
  latestRoundNumber: number;
  onClose: () => void;
  requestId: string;
  viewingRoundNumber: number;
}) => {
  const { t } = useTranslation('explore');
  const { isNavbarOpen } = useSidebars();
  const navigateTo = useNavigateTo();
  const { isSubmitting, submitWithTruncateGuard } = useSubmitExploreRound({
    latestRoundNumber,
    requestId,
    viewingRoundNumber,
  });

  const {
    updatedLocations,
    updatedExampleCompanies,
    updatedDescription,
    setUpdatedExampleCompanies,
    setUpdatedLocations,
    setDefaultValues,
    resetForm,
    checkIsDirty,
  } = useRegenerateExploreForm();
  const [modal, setModalState] = useAtom(modalState);
  const leftOffset = isNavbarOpen ? OPEN_NAVBAR_WIDTH : CLOSED_NAVBAR_WIDTH;
  const isSelectingExampleCompanies =
    modal?.state === 'addExampleCompaniesToExploreForm';
  const isDirty = checkIsDirty(defaultValues);
  const pendingFilterDeltas = getPendingFilterDeltas({
    defaultExampleCompanies: defaultValues.defaultExampleCompanies,
    defaultLocations: defaultValues.defaultLocations,
    updatedExampleCompanies,
    updatedLocations,
  });
  const hasFilterChanges = pendingFilterDeltas.length > 0;
  const nextRoundNumber = viewingRoundNumber + 1;
  const isRoundCapReached = viewingRoundNumber >= MAX_EXPLORE_ROUNDS;

  useEffect(() => {
    if (isLoading) {
      return;
    }
    setDefaultValues(defaultValues);

    return () => {
      resetForm();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues, isLoading]);

  const handleClose = () => {
    if (
      isSelectingExampleCompanies ||
      modal?.state === 'confirmDiscardChanges' ||
      modal?.state === 'confirmExploreRoundTruncate'
    ) {
      return;
    }

    if (isDirty) {
      return setModalState({
        state: 'confirmDiscardChanges',
        mainAction: () => {
          resetForm();
          onClose();
        },
      });
    }
    onClose();
  };

  const handleSubmit = async () => {
    if (isRoundCapReached || isSubmitting || !hasFilterChanges) {
      return;
    }

    await submitWithTruncateGuard({
      defaultDescription: defaultValues.defaultDescription,
      defaultExampleCompanies: defaultValues.defaultExampleCompanies,
      defaultLocations: defaultValues.defaultLocations,
      onBeforeSubmit: () => {
        resetForm();
        onClose();
      },
      reopenPanelOnTruncateConflict: true,
      updatedDescription,
      updatedExampleCompanies,
      updatedLocations,
    });
  };

  return (
    <ContentWrapper
      leftOffset={leftOffset}
      onOutsideClick={handleClose}
      scrimColor="rgba(99, 104, 139, 0.55)"
    >
      <Container name="regenerate-explore-results-panel" direction="column">
        <Flexbox
          name="regenerate-explore-results-header"
          justify="space-between"
          fullWidth
        >
          <HeaderTitle>{t`editSearchFilters`}</HeaderTitle>
          <Icon
            icon="ArrowLeftBack"
            width="24px"
            height="24px"
            color={colors.basics.black}
            onClick={handleClose}
          />
        </Flexbox>
        {isRoundCapReached ? (
          <CapBlocked gap="16px">
            <CapText>{t`roundCapBlocked`}</CapText>
            <Button
              onClick={() => {
                resetForm();
                onClose();
                navigateTo.explore();
              }}
            >{t`startNewSearch`}</Button>
          </CapBlocked>
        ) : (
          <Form gap="20px">
            <FilterNote>{t`filterRoundNote`}</FilterNote>
            <Stack gap="8px">
              <Question>{t`locationQuestionTitle`}</Question>
              <LocationSelect
                backgroundColor={filterFieldBackground}
                borderColor={filterFieldBorderColor}
                borderRadius="8px"
                borderWidth="1px"
                defaultValue={updatedLocations}
                onChange={option => {
                  if (!isDefaultOptionArray(option) || option.length < 0)
                    return;
                  setUpdatedLocations(option);
                }}
                placeholder={t`locationPlaceholder`}
              />
            </Stack>
            <Stack gap="8px">
              <Question>{t`exampleCompaniesQuestionTitle`}</Question>
              <ExampleCompaniesSelect
                exampleCompanies={updatedExampleCompanies}
                fieldBackground={filterFieldBackground}
                setExampleCompanies={setUpdatedExampleCompanies}
                withBorder
              />
            </Stack>
            <FilterDelta>
              {hasFilterChanges ? (
                <>
                  <FilterDeltaLine>
                    {t`filterCreatesRoundPrefix`}{' '}
                    <FilterDeltaBold>
                      {t('filterRoundLabel', { roundNumber: nextRoundNumber })}
                    </FilterDeltaBold>
                    :
                  </FilterDeltaLine>
                  <FilterDeltaSummary>
                    {pendingFilterDeltas
                      .map(delta =>
                        t(delta.kind, { items: delta.items.join(', ') }),
                      )
                      .join(' · ')}
                  </FilterDeltaSummary>
                </>
              ) : (
                t`filterNoChangesYet`
              )}
            </FilterDelta>
            <SubmitButton
              onClick={() => {
                void handleSubmit();
              }}
              disabled={!hasFilterChanges || isSubmitting}
            >
              {isSubmitting
                ? t`refining`
                : hasFilterChanges
                  ? t('applyAsRound', { roundNumber: nextRoundNumber })
                  : t`applyAsNewRound`}
            </SubmitButton>
          </Form>
        )}
      </Container>
    </ContentWrapper>
  );
};

const ContentWrapper = styled(FixedPanelWithShadowLayer)`
  background-color: ${({ theme }) => theme.colors.basics.white};

  @media (min-width: 1600px) {
    min-width: unset;
    max-width: 680px;
  }
`;

const Container = styled(Flexbox)`
  padding: 33px 20px 33px 33px;
`;

const HeaderTitle = styled('h1')`
  ${({ theme }) => theme.typography.widget.emptyState};
  margin-left: 12px;
`;

const Form = styled(Stack)`
  margin-top: 10px;
  padding-right: 10px;
  max-height: calc(100vh - 100px);
  ${({ theme }) => theme.mixins.scrollbar};
  overflow-y: auto;
`;

const FilterNote = styled('p')`
  margin: 0 0 22px;
  ${({ theme }) => theme.typography.acrossPlatform.tiny};
  color: ${({ theme }) => theme.colors.gray.c11};
  line-height: 1.5;
`;

const FilterDelta = styled('div')`
  background: ${({ theme }) => theme.colors.gray.c1};
  border: 1px solid ${({ theme }) => theme.colors.gray.c3};
  border-radius: 9px;
  padding: 12px 14px;
  ${({ theme }) => theme.typography.acrossPlatform.tiny};
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.gray.c12};
`;

const FilterDeltaLine = styled('div')`
  color: ${({ theme }) => theme.colors.gray.c12};
`;

const FilterDeltaBold = styled('span')`
  color: ${({ theme }) => theme.colors.basics.black};
  font-weight: 700;
`;

const FilterDeltaSummary = styled('div')`
  margin-top: 4px;
  color: ${({ theme }) => theme.colors.gray.c12};
`;

const Question = styled('div')`
  ${({ theme }) => theme.typography.acrossPlatform.small};
  color: ${({ theme }) => theme.colors.gray.c12};
`;

const CapBlocked = styled(Stack)`
  margin-top: 24px;
`;

const CapText = styled('p')`
  ${({ theme }) => theme.typography.acrossPlatform.small};
  color: ${({ theme }) => theme.colors.gray.c12};
`;

const SubmitButton = styled(Button)`
  margin-left: auto;
`;
